import React, { useState, useEffect } from "react";
import {
  LucideX,
  Download,
  Eye,
} from "lucide-react";
import { handleApiError } from "../../../common/utils";
import { toast } from "react-toastify";
import { usePremisesStore, useRegionStore } from "../../../store/store";
import { useS3Upload, S3TempUrl, getSignedUrlForS3 } from "../../../utils/s3Client";
import DocumentViewer from "../../../components/DocumentViewer/DocumentViewer";
import PdfViewer from "../../../components/pdf/PdfViewer";
import { XIcon } from "lucide-react";
import { usePermissions } from "../../../hooks/usePermission";
import { INCIDENTS_MODULE } from "../../../constants/modules";
import {
  CreateIncidentFormProps,
  DropdownOption,
  Attachment,
  FilePreviewState,
} from "../types/types";
import {
  getIncidentById,
  createIncident,
  updateIncident,
} from "../services/service";
import {
  getFileExtension,
  getFileIcon,
  canPreviewFile,
} from "../utils/utils";

const CreateIncidentForm: React.FC<CreateIncidentFormProps> = ({
  departmentName,
  onSuccess,
  mode = "create",
  incidentId,
  moduleKey,
}) => {
  const { canCreate, canUpdate } = usePermissions();

  // Dynamic permission check based on mode and moduleKey
  const hasPermission = mode === 'create'
    ? canCreate(moduleKey || INCIDENTS_MODULE)
    : canUpdate(moduleKey || INCIDENTS_MODULE);
  const [formData, setFormData] = useState({
    regionId: "",
    premisesId: "",
    reportDate: "",
    occurrenceDate: "",
    reportedBy: "",
    placeOfOccurrence: "",
    department: departmentName || "",
    whatWentWrong: "",
    incidentEventType: "",
    incidentEventTypeOther: "",
    severity: "",
  });

  const [files, setFiles] = useState<File[] | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [existingAttachments, setExistingAttachments] = useState<Attachment[]>(
    []
  );
  const [uploadErrorMessage, setUploadErrorMessage] = useState("");

  // Document preview states
  const [fileUrls, setFileUrls] = useState<FilePreviewState[]>([]);
  const [errorMessage, setErrorMessage] = useState("");
  const [fileurl, setFileUrl] = useState<FilePreviewState>({
    url: "",
    name: "",
    type: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isDocumentViewerOpen, setIsDocumentViewerOpen] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  const { getsignedurl } = S3TempUrl();

  const {
    uploadFileToS3,
    progress: uploadProgress,
    uploading,
    error: uploadError,
  } = useS3Upload();

  const [regionOptions, setRegionOptions] = useState<DropdownOption[]>([]);
  const [hospitalOptions, setHospitalOptions] = useState<DropdownOption[]>([]);
  const { allPremises, fetchPremises } = usePremisesStore();
  const { regions, fetchRegions } = useRegionStore();

  // ==================== FILE HANDLERS ====================

  const handleDownloadFile = (attachment: Attachment) => {
    const filename =
      attachment.filename ||
      attachment.name ||
      attachment.originalName ||
      "download";
    const link = document.createElement("a");
    link.href = getSignedUrlForS3(attachment.url);
    link.download = filename;
    link.target = "_blank";
    link.rel = "noopener noreferrer";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handlePreviewFile = async (attachment: Attachment) => {
    const filename =
      attachment.filename || attachment.name || attachment.originalName || "";
    if (canPreviewFile(filename)) {
      try {
        setIsLoading(true);
        const fileurl = await getsignedurl(
          filename,
          getFileExtension(filename)
        );
        setFileUrl({
          url: fileurl,
          name: filename,
          type: getFileExtension(filename),
        });
        setIsDocumentViewerOpen(true);
      } catch {
        setErrorMessage("File not found");
      } finally {
        setIsLoading(false);
      }
    } else {
      toast.info(
        "Preview not available for this file type. Use download instead."
      );
    }
  };

  const handleCloseDocumentViewer = () => {
    setIsDocumentViewerOpen(false);
    setFileUrl({ url: "", name: "", type: "" });
    setErrorMessage("");
  };

  const handleRemoveExistingAttachment = (index: number) => {
    if (mode === "view") return;
    setExistingAttachments((prev) => {
      const updated = [...prev];
      updated.splice(index, 1);
      return updated;
    });
  };

  // ==================== REGION / PREMISES HELPERS ====================

  const getRegionIdByName = (regionName: string) => {
    const region = regions.find(
      (r: any) => r.regionName?.trim() === regionName
    );
    return region?._id || "";
  };

  const getHospitalIdByName = (hospitalName: string) => {
    const hospital = allPremises.find(
      (p: any) => p.premisesName?.trim() === hospitalName
    );
    return hospital?.id || hospital?._id || "";
  };

  useEffect(() => {
    fetchPremises();
    if (!regions.length) fetchRegions();
  }, []);

  useEffect(() => {
    if (allPremises.length > 0 && regions.length > 0) {
      const uniqueRegions = regions.map((region: any) => ({
        id: region._id,
        name: region?.regionName?.trim(),
      }));
      setRegionOptions(uniqueRegions);
      updateHospitalOptions(formData.regionId);
    }
  }, [allPremises, regions]);

  useEffect(() => {
    if (allPremises.length > 0) {
      updateHospitalOptions(formData.regionId);
    }
  }, [formData.regionId, allPremises]);

  const updateHospitalOptions = (selectedRegionId: string) => {
    if (!selectedRegionId) {
      const uniqueFacilities: DropdownOption[] = Array.from(
        new Set(
          allPremises.map((premise: any) =>
            JSON.stringify({
              id: premise._id,
              name: premise.premisesName.trim(),
            })
          )
        )
      ).map((item: string) => JSON.parse(item));
      setHospitalOptions(uniqueFacilities);
    } else {
      const filteredFacilities = allPremises
        .filter((premise: any) => premise.region === selectedRegionId)
        .map((premise: any) => ({
          id: premise._id,
          name: premise.premisesName.trim(),
        }));
      const uniqueFacilities: DropdownOption[] = Array.from(
        new Set(filteredFacilities.map((f) => JSON.stringify(f)))
      ).map((item) => JSON.parse(item));
      setHospitalOptions(uniqueFacilities);
    }
  };

  // ==================== FETCH INCIDENT DATA ====================

  useEffect(() => {
    if ((mode === "view" || mode === "update") && incidentId) {
      fetchIncidentData();
    }
  }, [mode, incidentId]);

  const fetchIncidentData = async () => {
    setLoading(true);
    try {
      const response = await getIncidentById(incidentId!);
      const incident = response.data;

      const regionId =
        incident.region && incident.region.length === 24
          ? incident.region
          : getRegionIdByName(incident.region || "");

      const premisesId =
        incident.hospitalName && incident.hospitalName.length === 24
          ? incident.hospitalName
          : getHospitalIdByName(incident.hospitalName || "");

      const moduleData = incident.moduleSpecificData || {};

      setFormData({
        regionId,
        premisesId,
        reportDate: incident.reportDate
          ? incident.reportDate.includes("T")
            ? incident.reportDate.slice(0, 16)
            : incident.reportDate
          : "",
        occurrenceDate: incident.incidentDate
          ? incident.incidentDate.includes("T")
            ? incident.incidentDate.slice(0, 16)
            : incident.incidentDate
          : "",
        reportedBy: incident.reportedBy || "",
        placeOfOccurrence: incident.placeOfOccurrence || "",
        department: incident.department || "",
        whatWentWrong: incident.whatWentWrong || "",
        incidentEventType: moduleData.incidentEventType || "",
        incidentEventTypeOther: moduleData.incidentEventTypeOther || "",
        severity: incident.severity || "",
      });

      if (incident.attachments && incident.attachments.length > 0) {
        const normalizedAttachments = incident.attachments.map(
          (attachment: any) => {
            if (typeof attachment === "string") {
              return {
                url: attachment,
                filename: attachment.split("/").pop() || "Unknown file",
              };
            }
            return {
              url: attachment.url || attachment,
              filename:
                attachment.filename ||
                attachment.name ||
                attachment.originalName ||
                attachment.url?.split("/").pop() ||
                "Unknown file",
            };
          }
        );
        setExistingAttachments(normalizedAttachments);
        setFileUrls(
          normalizedAttachments.map((a: any) => ({
            name: a.filename,
            url: a.url,
            type: getFileExtension(a.filename),
          }))
        );
      }
    } catch (error) {
      handleApiError(error, "Failed to fetch incident data");
    } finally {
      setLoading(false);
    }
  };

  // ==================== CHANGE HANDLERS ====================

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    if (mode === "view") return;
    const { name, value } = e.target;
    setFormData((prev) => {
      const updated = { ...prev, [name]: value };
      if (name === "regionId") {
        updated.premisesId = "";
      }
      // Clear "Other" text when type changes away from Other
      if (name === "incidentEventType" && value !== "Other") {
        updated.incidentEventTypeOther = "";
      }
      return updated;
    });
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (mode === "view") return;
    if (e.target.files) {
      const maxSize = 5 * 1024 * 1024;
      const validFiles = Array.from(e.target.files).filter(
        (file) => file.size <= maxSize
      );
      if (validFiles.length !== e.target.files.length) {
        setUploadErrorMessage("Some files exceed the 5MB size limit.");
      }
      setFiles((prev) =>
        prev ? [...prev, ...validFiles] : validFiles.length > 0 ? validFiles : null
      );
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (mode === "view") return;
    const droppedFiles = Array.from(e.dataTransfer.files);
    const maxSize = 5 * 1024 * 1024;
    const validFiles = droppedFiles.filter((file) => file.size <= maxSize);
    if (validFiles.length !== droppedFiles.length) {
      setUploadErrorMessage("Some files exceed the 5MB size limit.");
    }
    if (validFiles.length > 0) {
      setFiles((prev) => (prev ? [...prev, ...validFiles] : validFiles));
    }
  };

  const handleRemoveFile = (index: number) => {
    if (!files) return;
    setFiles((prev) => {
      const updatedFiles = [...prev!];
      updatedFiles.splice(index, 1);
      return updatedFiles.length > 0 ? updatedFiles : null;
    });
    setUploadErrorMessage("");
  };

  // ==================== SUBMIT ====================

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (mode === "view") return;

    const missingFields: string[] = [];
    if (!formData.regionId) missingFields.push("Region");
    if (!formData.premisesId) missingFields.push("Premises");
    if (!formData.reportDate) missingFields.push("Incident Reporting Date & Time");
    if (!formData.occurrenceDate)
      missingFields.push("Incident Occurrence Date & Time");
    if (!formData.reportedBy) missingFields.push("Incident Reported By");
    if (!formData.placeOfOccurrence) missingFields.push("Place Of Occurrence");
    if (!formData.department) missingFields.push("Department Name");
    if (!formData.whatWentWrong) missingFields.push("Brief Description of Incident");
    if (!formData.severity) missingFields.push("Severity");
    if (!formData.incidentEventType)
      missingFields.push("Type of Incident Event");
    if (
      formData.incidentEventType === "Other" &&
      !formData.incidentEventTypeOther
    )
      missingFields.push("Specify Incident Event Type");

    if (missingFields.length > 0) {
      toast.error(
        `Please fill in all required fields: ${missingFields.join(", ")}`
      );
      return;
    }

    if (
      mode === "update" &&
      (!incidentId ||
        typeof incidentId !== "string" ||
        incidentId.length !== 24)
    ) {
      toast.error("Invalid incident ID");
      setSubmitting(false);
      return;
    }

    setSubmitting(true);
    setUploadErrorMessage("");

    let s3FileUrls: string[] = [];
    try {
      if (files && files.length > 0) {
        for (const file of files) {
          const s3Url = await uploadFileToS3(file);
          if (s3Url) {
            s3FileUrls.push(s3Url);
          } else {
            throw new Error(`Failed to upload file: ${file.name}`);
          }
        }
      }
    } catch (error) {
      setUploadErrorMessage(`File upload failed: ${(error as Error).message}`);
      setSubmitting(false);
      return;
    }

    try {
      let response;

      const payload: Record<string, any> = {
        region: formData.regionId,
        hospitalName: formData.premisesId,
        reportDate: formData.reportDate,
        incidentDate: formData.occurrenceDate,
        reportedBy: formData.reportedBy,
        placeOfOccurrence: formData.placeOfOccurrence,
        department: formData.department,
        sourceModule: departmentName || formData.department,
        whatWentWrong: formData.whatWentWrong,
        severity: formData.severity,
        moduleSpecificData: {
          incidentEventType: formData.incidentEventType,
          incidentEventTypeOther: formData.incidentEventTypeOther,
        },
        attachments: [
          ...existingAttachments.map((a) => a.url || a),
          ...s3FileUrls,
        ],
      };

      if (mode === "update") {
        response = await updateIncident(incidentId!, payload);
      } else {
        response = await createIncident(payload);
      }

      const successMessage =
        mode === "update"
          ? "Incident updated successfully"
          : "Incident created successfully";
      toast.success(successMessage);
      if (onSuccess) onSuccess();

      if (mode === "create") {
        resetForm();
      }
    } catch (error) {
      if (
        (error as any).response?.status >= 200 &&
        (error as any).response?.status < 300
      ) {
        toast.success(
          mode === "update"
            ? "Incident updated successfully"
            : "Incident created successfully"
        );
        if (onSuccess) onSuccess();
        return;
      }
      if ((error as any).response?.status >= 400) {
        toast.error(
          (error as any).response?.data?.message ||
          (mode === "update"
            ? "Failed to update incident"
            : "Failed to create incident")
        );
      } else {
        handleApiError(
          error,
          mode === "update"
            ? "Network error occurred while updating incident"
            : "Network error occurred while creating incident"
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      regionId: "",
      premisesId: "",
      reportDate: "",
      occurrenceDate: "",
      reportedBy: "",
      placeOfOccurrence: "",
      department: departmentName || "",
      whatWentWrong: "",
      incidentEventType: "",
      incidentEventTypeOther: "",
      severity: "",
    });
    setFiles(null);
    setExistingAttachments([]);
  };

  const isReadOnly = mode === "view";
  const isDisabled = mode === "view" || submitting || uploading;

  if (loading) {
    return (
      <div className="flex flex-col justify-center items-center p-12">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-teal-600 mb-4" />
        <p className="text-gray-600 font-medium">Loading incident data...</p>
      </div>
    );
  }

  // ==================== FORM ====================

  return (
    <>
      {(uploading || submitting) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1200]">
          <div className="bg-white p-6 rounded-lg shadow-lg max-w-md w-full text-center">
            <div className="flex justify-center mb-4">
              <svg
                className="animate-spin h-8 w-8 text-teal-600"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
            </div>
            <h3 className="text-lg font-medium mb-2">
              {uploading ? "Uploading files..." : "Submitting..."}
            </h3>
            {uploading && (
              <p className="text-sm text-gray-600">
                Progress: {uploadProgress}%
              </p>
            )}
          </div>
        </div>
      )}

      <form
        onSubmit={handleSubmit}
        id="fireIncidentCreateForm"
        className="flex flex-col h-full"
      >
        <div className="flex-1 overflow-y-auto px-8 py-6 space-y-8">
          {uploadErrorMessage && (
            <div className="p-2 bg-red-100 text-red-700 rounded">
              {uploadErrorMessage}
            </div>
          )}

          {/* Section: Incident Details */}
          <div>
            <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide border-b border-gray-200 pb-2 mb-4">
              Incident Details
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5">
              {/* Region */}
              <div>
                <label className="block mb-1 text-black">
                  Region <span className="text-red-500">*</span>
                </label>
                <select
                  name="regionId"
                  value={formData.regionId}
                  onChange={handleChange}
                  required={mode !== "view"}
                  disabled={isDisabled}
                  className="w-full border-2 border-gray-300 p-2"
                >
                  <option value="">Select Region</option>
                  {regionOptions.map((opt) => (
                    <option key={opt.id} value={opt.id}>
                      {opt.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Premises */}
              <div>
                <label className="block mb-1 text-black">
                  Premises <span className="text-red-500">*</span>
                </label>
                <select
                  name="premisesId"
                  value={formData.premisesId}
                  onChange={handleChange}
                  required={mode !== "view"}
                  disabled={isDisabled}
                  className="w-full border-2 border-gray-300 p-2"
                >
                  <option value="">Select Premises</option>
                  {hospitalOptions.map((opt) => (
                    <option key={opt.id} value={opt.id}>
                      {opt.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Incident Reporting Date & Time */}
              <div>
                <label className="block mb-1 text-black">
                  Incident Reporting Date & Time{" "}
                  <span className="text-red-500">*</span>
                </label>
                <input
                  type="datetime-local"
                  name="reportDate"
                  value={formData.reportDate}
                  onChange={handleChange}
                  required={mode !== "view"}
                  disabled={isDisabled}
                  readOnly={isReadOnly}
                  className="w-full border-2 border-gray-300 p-2"
                />
              </div>

              {/* Incident Occurrence Date & Time */}
              <div>
                <label className="block mb-1 text-black">
                  Incident Occurrence Date & Time{" "}
                  <span className="text-red-500">*</span>
                </label>
                <input
                  type="datetime-local"
                  name="occurrenceDate"
                  value={formData.occurrenceDate}
                  onChange={handleChange}
                  required={mode !== "view"}
                  disabled={isDisabled}
                  readOnly={isReadOnly}
                  className="w-full border-2 border-gray-300 p-2"
                />
              </div>

              {/* Incident Reported By */}
              <div>
                <label className="block mb-1 text-black">
                  Incident Reported By{" "}
                  <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="reportedBy"
                  value={formData.reportedBy}
                  onChange={handleChange}
                  placeholder="Enter name of person reporting"
                  required={mode !== "view"}
                  disabled={isDisabled}
                  readOnly={isReadOnly}
                  className="w-full border-2 border-gray-300 p-2"
                />
              </div>

              {/* Place of Occurrence */}
              <div>
                <label className="block mb-1 text-black">
                  Place of Occurrence{" "}
                  <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="placeOfOccurrence"
                  value={formData.placeOfOccurrence}
                  onChange={handleChange}
                  placeholder="e.g., Building A, Floor 2, Room 201"
                  required={mode !== "view"}
                  disabled={isDisabled}
                  readOnly={isReadOnly}
                  className="w-full border-2 border-gray-300 p-2"
                />
              </div>

              {/* Department Name */}
              <div>
                <label className="block mb-1 text-black">
                  Department Name{" "}
                  <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="department"
                  value={formData.department}
                  onChange={handleChange}
                  readOnly={!!departmentName || isReadOnly}
                  required={mode !== "view"}
                  disabled={isDisabled}
                  className="w-full border-2 border-gray-300 p-2 bg-gray-50"
                />
              </div>

              {/* Type of Incident Event */}
              <div>
                <label className="block mb-1 text-black">
                  Type of Incident Event{" "}
                  <span className="text-red-500">*</span>
                </label>
                <select
                  name="incidentEventType"
                  value={formData.incidentEventType}
                  onChange={handleChange}
                  required={mode !== "view"}
                  disabled={isDisabled}
                  className="w-full border-2 border-gray-300 p-2"
                >
                  <option value="Fire">Fire</option>
                  <option value="Work At Height">Work At Height</option>
                  <option value="Confined Space">Confined Space</option>
                  <option value="Slip/ Trip Hazard">Slip/ Trip Hazard</option>
                  <option value="Chemical Spillage">Chemical Spillage</option>
                  <option value="Hazardous waste">Hazardous waste</option>
                  <option value="Clinical Incident">Clinical Incident</option>
                  <option value="Non-Clinical Incident">Non-Clinical Incident</option>
                  <option value="Safety Incident">Safety Incident (Fire & General Safety)</option>
                  <option value="Security Incident">Security Incident</option>
                  <option value="Sentinel Event">Sentinel Event</option>
                  <option value="Near Miss Incident">Near Miss Incident</option>
                  <option value="Code-based Incidents">Disaster / Code-based Incidents</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              {/* "Other" Incident Type Text */}
              {formData.incidentEventType === "Other" && (
                <div className="md:col-span-2">
                  <label className="block mb-1 text-black">
                    Specify Incident Type{" "}
                    <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="incidentEventTypeOther"
                    value={formData.incidentEventTypeOther}
                    onChange={handleChange}
                    placeholder="Describe the type of incident event"
                    required={mode !== "view"}
                    disabled={isDisabled}
                    readOnly={isReadOnly}
                    className="w-full border-2 border-gray-300 p-2"
                  />
                </div>
              )}

              {/* Severity */}
              <div>
                <label className="block mb-1 text-black">
                  Severity <span className="text-red-500">*</span>
                </label>
                <select
                  name="severity"
                  value={formData.severity}
                  onChange={handleChange}
                  required={mode !== "view"}
                  disabled={isDisabled}
                  className="w-full border-2 border-gray-300 p-2"
                >
                  <option value="">Select Severity</option>
                  <option value="Low">Low</option>
                  <option value="Medium">Medium</option>
                  <option value="High">High</option>
                  <option value="Critical">Critical</option>
                </select>
              </div>
            </div>
          </div>

          {/* Section: Description */}
          <div>
            <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide border-b border-gray-200 pb-2 mb-4">
              Description
            </h4>
            <div className="space-y-5">
              <div>
                <label className="block mb-1 text-black">
                  Brief Description of Incident{" "}
                  <span className="text-red-500">*</span>
                </label>
                <textarea
                  name="whatWentWrong"
                  value={formData.whatWentWrong}
                  onChange={handleChange}
                  placeholder="Provide a brief description of what happened during the incident..."
                  required={mode !== "view"}
                  disabled={isDisabled}
                  readOnly={isReadOnly}
                  rows={4}
                  className="w-full border-2 border-gray-300 p-2 resize-y"
                />
              </div>
            </div>
          </div>

          {/* Section: Attachments */}
          <div>
            <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide border-b border-gray-200 pb-2 mb-4">
              Attachments
            </h4>
            <div className="space-y-4">
              {/* Existing Attachments */}
              {(mode === "view" || mode === "update") &&
                existingAttachments.length > 0 && (
                  <div>
                    <p className="text-sm font-medium mb-2">
                      Existing Attachments:
                    </p>
                    <div className="space-y-2">
                      {existingAttachments.map((attachment, index) => {
                        const filename =
                          attachment.filename ||
                          attachment.name ||
                          attachment.originalName ||
                          attachment.url?.split("/").pop() ||
                          `Attachment ${index + 1}`;
                        return (
                          <div
                            key={index}
                            className="flex items-center justify-between bg-gray-50 p-3 rounded border"
                          >
                            <div className="flex items-center space-x-2 flex-1">
                              {getFileIcon(filename)}
                              <span className="text-sm text-gray-700 truncate max-w-xs">
                                {filename}
                              </span>
                            </div>
                            <div className="flex items-center space-x-2">
                              {canPreviewFile(filename) && (
                                <button
                                  type="button"
                                  onClick={() =>
                                    handlePreviewFile(attachment)
                                  }
                                  className="p-1.5 text-green-600 hover:bg-green-100 rounded transition-colors"
                                  title="Preview file"
                                >
                                  <Eye size={16} />
                                </button>
                              )}
                              <button
                                type="button"
                                onClick={() =>
                                  handleDownloadFile(attachment)
                                }
                                className="p-1.5 text-blue-600 hover:bg-blue-100 rounded transition-colors"
                                title="Download file"
                              >
                                <Download size={16} />
                              </button>
                              {mode === "update" && (
                                <button
                                  type="button"
                                  onClick={() =>
                                    handleRemoveExistingAttachment(index)
                                  }
                                  className="p-1.5 text-red-600 hover:bg-red-100 rounded transition-colors"
                                  title="Remove attachment"
                                  disabled={isDisabled}
                                >
                                  <LucideX size={16} />
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

              {/* Upload Area */}
              {mode !== "view" && (
                <div>
                  <span className="flex justify-between mb-2">
                    <p className="text-sm text-gray-600">
                      Supported:{" "}
                      <span className="text-orange-600">
                        DOC/DOCX, PDF, TXT, JPG, PNG
                      </span>{" "}
                      Max size: <span className="text-orange-600">5 MB</span>
                    </p>
                  </span>
                  <label
                    htmlFor="fire-file-upload"
                    className={`block border-2 border-dashed border-gray-300 p-6 text-center text-blue-700 cursor-pointer hover:bg-blue-50 ${isDisabled ? "opacity-50 cursor-not-allowed" : ""
                      }`}
                    onDrop={handleDrop}
                    onDragOver={(e) => {
                      e.preventDefault();
                      setIsDragging(true);
                    }}
                    onDragLeave={() => setIsDragging(false)}
                  >
                    {files && files.length > 0 ? (
                      <div className="space-y-2">
                        {files.map((file, index) => (
                          <div
                            key={index}
                            className="text-gray-800 bg-gray-100 px-3 py-1 rounded text-sm flex justify-between items-center"
                          >
                            <span className="truncate w-3/4 text-left">
                              {file.name}
                            </span>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleRemoveFile(index);
                              }}
                              className="text-red-500 hover:text-red-600"
                              disabled={isDisabled}
                            >
                              <LucideX size={16} />
                            </button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      "Drag & Drop Your files here Or Click"
                    )}
                  </label>
                  <input
                    id="fire-file-upload"
                    type="file"
                    className="hidden"
                    multiple
                    accept=".doc,.docx,.pdf,.txt,.jpg,.jpeg,.png"
                    onChange={handleFileChange}
                    disabled={isDisabled}
                  />
                  {uploading && (
                    <div className="mt-2">
                      <p>Uploading: {uploadProgress}%</p>
                      <div className="w-full bg-gray-200 rounded-full h-2.5">
                        <div
                          className="bg-blue-600 h-2.5 rounded-full"
                          style={{ width: `${uploadProgress}%` }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Form Actions */}
        {mode !== "view" && (
          <div className="border-t px-8 py-4 bg-[#eef4f8] flex justify-start flex-shrink-0">
            <button
              type="submit"
              className="bg-teal-600 text-white py-2 px-10 rounded-full hover:bg-teal-500 disabled:bg-gray-400 disabled:cursor-not-allowed font-semibold"
              disabled={isDisabled || !hasPermission}
            >
              {submitting || uploading
                ? mode === "update"
                  ? "UPDATING..."
                  : "SUBMITTING..."
                : mode === "update"
                  ? "UPDATE"
                  : "SUBMIT"}
            </button>
          </div>
        )}
      </form>

      {/* Document Viewer Modal */}
      {isDocumentViewerOpen && (
        <div className="fixed inset-0 z-[1100] flex items-center justify-center bg-black bg-opacity-45">
          <div className="relative bg-white rounded-lg shadow-xl w-[90vw] h-[90vh] flex flex-col">
            <div className="absolute right-4 top-4 z-10">
              <button
                onClick={handleCloseDocumentViewer}
                className="bg-red-500 hover:bg-red-600 text-white p-2 rounded-full shadow-lg transition-colors"
                aria-label="Close document viewer"
              >
                <XIcon size={20} />
              </button>
            </div>
            <div className="flex-1 p-4 overflow-auto">
              {errorMessage && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                  {errorMessage}
                </div>
              )}
              {isLoading ? (
                <div className="flex justify-center items-center h-full">
                  <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500" />
                </div>
              ) : fileurl.url ? (
                fileurl.type === "pdf" ? (
                  <PdfViewer fileUrl={fileurl.url} />
                ) : (
                  <DocumentViewer file={fileurl.url} name={fileurl.name} />
                )
              ) : (
                <div className="flex justify-center items-center h-full">
                  <p>No document to display</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CreateIncidentForm;
